/**
 * Reclassify the backend's `CourseBuckets` into the three buckets we render:
 *
 *   - `not_started` — cards the learner hasn't touched: backend `in_progress`
 *                     cards with zero progress AND backend `upcoming` cards
 *                     (not-yet-open windows). The card caption distinguishes
 *                     them ("Started Aug 31" vs "Starts Sep 5") — Aleix, 8/31.
 *   - `in_progress` — backend `in_progress` cards still inside their date window
 *   - `finalized`   — backend `completed` cards PLUS any in-progress/upcoming
 *                     card whose `end_date` has passed (the learner ran out of
 *                     time before completing). The Finalized bucket is the
 *                     unified "this enrolment is closed" group.
 *
 * The helper is pure — pass it the raw `CourseBuckets` payload from
 * `GET /me/dashboard` or `GET /me/courses` and consume the result.
 */

import type { components } from '../types/learnerApi';
import { getEnrolmentAvailability } from './enrolmentAvailability';

type CourseCard = components['schemas']['CourseCard'];
type CourseBuckets = components['schemas']['CourseBuckets'];

export type DisplayBucket = 'not_started' | 'in_progress' | 'finalized';

export interface DisplayBuckets {
  not_started: CourseCard[];
  in_progress: CourseCard[];
  finalized: CourseCard[];
}

/** True when the learner has not touched the course at all — the window may
 * be open ("in progress" to the platform) but not to the learner (Aleix, 8/31). */
export function isUntouched(card: CourseCard): boolean {
  const nested = (card as unknown as {
    progress?: { modules_completed?: number; progress_percent?: number };
  }).progress;
  const percent = nested?.progress_percent ?? card.progress_percent ?? 0;
  const modulesDone = nested?.modules_completed ?? card.modules_completed ?? 0;
  return percent === 0 && modulesDone === 0;
}

function isExpired(card: CourseCard): boolean {
  return (
    getEnrolmentAvailability(
      { start_date: card.start_date, end_date: card.end_date },
      { completed: false },
    ).kind === 'expired'
  );
}

export function toDisplayBuckets(raw: CourseBuckets | undefined | null): DisplayBuckets {
  const out: DisplayBuckets = { not_started: [], in_progress: [], finalized: [] };
  if (!raw) return out;
  for (const card of raw.in_progress ?? []) {
    if (isExpired(card)) out.finalized.push(card);
    else if (isUntouched(card)) out.not_started.push(card);
    else out.in_progress.push(card);
  }
  for (const card of raw.upcoming ?? []) {
    if (isExpired(card)) out.finalized.push(card);
    else out.not_started.push(card);
  }
  out.finalized.push(...(raw.completed ?? []));
  return out;
}
